import { flatMap } from 'lodash-es';

import type { ChangeProposal } from '@payaca/types/changeProposalTypes';
import type { Deal } from '@payaca/types/dealTypes';
import type { Invoice, InvoiceLine } from '@payaca/types/invoiceTypes';
import type { JobLineItemBase } from '@payaca/types/jobContentTypes';
import type { Job } from '@payaca/types/jobTypesV2';
import { isAnyUninvoicedValue } from './invoiceHelper';

export const canCreateInvoiceForDeal = (
  acceptedJobLineItems: JobLineItemBase[],
  existingInvoices: Invoice[],
  existingInvoiceLines: InvoiceLine[]
) => {
  const unvoidedInvoices = existingInvoices.filter((x) => !x.voidedAt);
  const unvoidedInvoiceLines = flatMap(unvoidedInvoices, (x) =>
    x.invoiceLineIds
      .map((y) => existingInvoiceLines.find((z) => z.id === y))
      .filter(Boolean)
  ) as InvoiceLine[];

  return isAnyUninvoicedValue(acceptedJobLineItems, unvoidedInvoiceLines);
};

export const canAddProposalToDeal = (deal?: Deal) => {
  if (!deal) {
    return false;
  }
  if (deal.version < 2) {
    return !deal?.archivedAt && !deal.propositionIds?.length;
  } else {
    return (
      !!deal &&
      !deal.archivedAt &&
      !deal.propositionIds?.length &&
      !deal.invoiceIds?.length
    );
  }
};

export const canAddChangeProposalToDeal = (
  deal: Deal | undefined,
  proposals: Job[],
  changeProposals: ChangeProposal[]
) => {
  if (!deal) {
    return false;
  }
  if (deal.version >= 2) {
    if (deal.archivedAt) return false;
    const acceptedProposal = acceptedProposalOnDeal(proposals);

    if (!acceptedProposal) return false;

    const pendingChangeProposal = changeProposals.find(
      (x) => !x.acceptedAt && !x.declinedAt && !x.voidedAt
    );

    if (pendingChangeProposal) return false;

    return true;
  } else {
    return false;
  }
};

export const acceptedProposalOnDeal = (proposals: Job[]) => {
  return proposals.find(
    (x) => x.acceptedAt && !x.archivedAt && !x.inactivatedAt
  );
};

export const buildEmailSubjectPrefixForProjectFromProject = (project: Deal) => {
  return buildEmailSubjectPrefixForProject({
    reference: project.customReference || project.reference?.toString() || '',
    siteAddress: {
      line1: project.siteAddresses?.[0]?.address?.line1 || undefined,
      line2: project.siteAddresses?.[0]?.address?.line2 || undefined,
    },
  });
};

export const buildEmailSubjectPrefixForProject = (project: {
  reference: string;
  siteAddress?: { line1?: string; line2?: string };
}) => {
  const addressLine = [project.siteAddress?.line1, project.siteAddress?.line2]
    .filter((elem) => {
      return !!elem?.length;
    })
    .join(', ');

  return `Ref: ${project.reference}${
    addressLine.length ? ` - ${addressLine}` : ``
  } - `;
};
